import path from 'node:path';
import pc from 'picocolors';
import { ApiClient, deriveBaseDomain } from '../client.js';
import { DEFAULT_PRODUCT_NAME, PROVISION_POLL_INTERVAL_MS } from '../constants.js';
import { readAppConfig } from '../lib/appConfig.js';
import { gitWireRemote } from '../lib/git.js';
import { assembleManifest } from '../lib/manifest.js';
import { renderStateTable, summarizeState, type StateResource } from '../lib/stateTable.js';
import { resolveProfile } from '../profiles.js';
import { emitJson, fail, human, note, ok, warn, type Io } from '../output.js';

interface ProvisionAccepted {
  slug: string;
  accepted: boolean;
  plan: Array<{ kind: string; name: string }>;
  statusUrl: string;
}

interface StateResponse {
  slug: string;
  resources: StateResource[];
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function planKey(kind: string, name: string): string {
  return `${kind} ${name}`;
}

export async function runProvision(options: {
  dir?: string;
  force?: boolean;
  json?: boolean;
  adminUrl?: string;
}): Promise<void> {
  const io: Io = { json: options.json === true, productName: DEFAULT_PRODUCT_NAME };
  const dir = path.resolve(process.cwd(), options.dir ?? '.');

  const resolved = resolveProfile(options.adminUrl);
  if (!resolved) fail(io, 'Not signed in. Run `ghosty login` first.', 1, 'NOT_LOGGED_IN');
  io.productName = resolved.profile.branding?.productName ?? DEFAULT_PRODUCT_NAME;

  let config;
  let manifest;
  let skippedBinary: string[];
  try {
    config = readAppConfig(dir);
    ({ manifest, skippedBinary } = assembleManifest(config, dir));
  } catch (err) {
    fail(io, (err as Error).message, 1, 'BAD_APP_TREE');
  }
  if (skippedBinary.length > 0) {
    warn(
      io,
      `Skipped ${skippedBinary.length} binary file(s) (can't ride in the provision manifest): ${skippedBinary.slice(0, 5).join(', ')}${skippedBinary.length > 5 ? ', …' : ''}. Push them with git after provisioning.`,
    );
  }

  const client = new ApiClient(resolved.adminUrl, resolved.profile.token);
  note(io, `Provisioning ${manifest.slug} on ${io.productName} (${manifest.files.length} files)...`);

  let accepted: ProvisionAccepted;
  try {
    accepted = await client.post<ProvisionAccepted>('/blocks/provision', {
      ...manifest,
      force: options.force === true,
    });
  } catch (err) {
    fail(io, (err as Error).message, 1, 'PROVISION_REJECTED');
  }
  if (io.json) emitJson({ event: 'accepted', slug: accepted.slug, plan: accepted.plan });

  const expected = new Set(accepted.plan.map((p) => planKey(p.kind, p.name)));
  let lastRender = '';

  // Poll until every planned block is ready, or any is failed.
  for (;;) {
    await sleep(PROVISION_POLL_INTERVAL_MS);
    let state: StateResponse;
    try {
      state = await client.get<StateResponse>(`/blocks/state/${manifest.slug}`);
    } catch (err) {
      note(io, `  (state poll failed: ${(err as Error).message} — retrying)`);
      continue;
    }
    const planned = state.resources.filter((r) => expected.has(planKey(r.kind, r.name)));
    const summary = summarizeState(planned);

    if (io.json) {
      emitJson({ event: 'state', slug: manifest.slug, summary, resources: planned });
    } else {
      const render = renderStateTable(planned);
      if (render !== lastRender) {
        lastRender = render;
        process.stderr.write(`\n${render}\n`);
      }
    }

    const failed = planned.find((r) => r.status === 'failed');
    if (failed) {
      const reason =
        typeof failed.metadata?.error === 'string' ? failed.metadata.error : 'unknown error';
      fail(
        io,
        `Provisioning failed at ${failed.kind} (${failed.name}): ${reason}. Fix the issue and re-run \`ghosty provision\` — completed steps are kept.`,
        1,
        'PROVISION_FAILED',
      );
    }

    if (summary.ready >= expected.size && planned.length >= expected.size) break;
  }

  // Success — gather URLs.
  const baseDomain = await (async () => {
    try {
      return deriveBaseDomain(resolved.adminUrl, await client.fetchConfig());
    } catch {
      return deriveBaseDomain(resolved.adminUrl);
    }
  })();
  const appUrl = `https://${manifest.slug}.${baseDomain}`;

  let repoUrl: string | null = null;
  let cloneUrl: string | null = null;
  try {
    const state = await client.get<StateResponse>(`/blocks/state/${manifest.slug}`);
    const repoRow = state.resources.find((r) => r.kind === 'repo.create');
    repoUrl = typeof repoRow?.metadata?.repoUrl === 'string' ? (repoRow.metadata.repoUrl as string) : null;
    cloneUrl = typeof repoRow?.metadata?.cloneUrl === 'string' ? (repoRow.metadata.cloneUrl as string) : null;
  } catch {
    /* non-fatal */
  }

  let gitNote: string | null = null;
  if (cloneUrl ?? repoUrl) {
    gitNote = gitWireRemote(dir, (cloneUrl ?? `${repoUrl}.git`) as string);
  }

  if (io.json) {
    emitJson({ ok: true, slug: manifest.slug, appUrl, repoUrl, git: gitNote });
    return;
  }
  human(io);
  ok(io, `${pc.bold(config.name)} is provisioned on ${io.productName}.`);
  human(io, `  App:  ${pc.bold(appUrl)}`);
  if (repoUrl) human(io, `  Repo: ${repoUrl}`);
  if (gitNote) note(io, `  ${gitNote}`);
  human(io);
  human(io, `${pc.green('ready')} — ${appUrl}`);
}
